import { ApiError } from "./api.js";

/**
 * Why /integrated could not load the subscription, in the answers the buyer can act on:
 *  - `link`: the link itself is no good (missing, expired, rejected by Zenpost, or for an account
 *    Zenpost does not know). Only a new link helps.
 *  - `temporary`: the server or the network failed; the same link may work a moment later.
 *  - `offer_expired`: a premium trial link (PIT-535) whose offer deadline passed. Nothing is wrong
 *    with the account and no new link is needed: the plan is managed from the app's billing page.
 */
export type IntegratedLoadErrorKind = "link" | "temporary" | "offer_expired";

export interface IntegratedLoadError {
  kind: IntegratedLoadErrorKind;
  /** Clarity event suffix: the HTTP status, `network` or `missing_token`. No personal data. */
  code: string;
}

export const MISSING_TOKEN_LOAD_ERROR: IntegratedLoadError = { kind: "link", code: "missing_token" };

export function classifyIntegratedLoadError(err: unknown): IntegratedLoadError {
  if (err instanceof ApiError) {
    const code = String(err.status);
    // 410 is only POST /offer-session's offer_expired (PIT-535): the offer's deadline passed.
    if (err.status === 410) return { kind: "offer_expired", code };
    // 400 is a malformed token (or language), 401 an expired or revoked one, 404 an account Zenpost
    // does not return (or a premium trial offer that does not exist). 404 also covers a Zenpost
    // outage (the lookup swallows the error), which a new link would not fix, but the server cannot
    // tell the two apart today.
    if (err.status === 400 || err.status === 401 || err.status === 404) return { kind: "link", code };
    return { kind: "temporary", code };
  }
  return { kind: "temporary", code: "network" };
}

export function integratedLoadErrorEventName(error: IntegratedLoadError): string {
  return `integrated_load_error_${error.code}`;
}

// ---------------------------------------------------------------------------
// The premium trial link (PIT-535): `/integrated?offer=<token>` instead of `?token=`.
// ---------------------------------------------------------------------------

/**
 * The offer token the page was opened with (`offer` in the URL, which stays there: a reload or a
 * copied address opens the same page). A `token` in the URL is a full billing session and wins, as
 * before.
 */
export function readPremiumTrialOfferToken(search: string): string {
  const params = new URLSearchParams(search);
  if (params.get("token")) return "";
  return params.get("offer") ?? "";
}

/**
 * The billing session the offer link opens lasts 2 h (401 `invalid_token` after that). Runs the
 * request with the current token and, on a 401, trades the same offer for a fresh session once and
 * runs it again. Without an offer, or on any other error, the error goes to the caller unchanged.
 */
export async function withOfferSessionRetry<T>(args: {
  offerToken: string;
  currentToken: () => string;
  refresh: () => Promise<string>;
  run: (token: string) => Promise<T>;
}): Promise<T> {
  try {
    return await args.run(args.currentToken());
  } catch (err) {
    if (!args.offerToken || !(err instanceof ApiError) || err.status !== 401) throw err;
    const fresh = await args.refresh();
    return args.run(fresh);
  }
}
